import { useEffect, useState } from 'react'
import { useRegisterSW } from 'virtual:pwa-register/react'
import { Button, Hinweisdialog } from './primitives'
import { AenderungenDialog } from './AenderungenDialog'
import { ContentUpdatePrompt } from './ContentUpdatePrompt'
import { NeuerungenPrompt } from './NeuerungenPrompt'
import { PushPrompt } from './PushPrompt'
import { anzahlUpdates } from '@/features/updates/diff'
import { sollPushFragen } from '@/features/updates/push'
import { ermittleNeuerungen } from '@/features/changelog/gesehen'
import type { Aenderung } from '@/features/changelog/fassungen'
import { useApp } from '@/store/app'

/**
 * Der Hinweisdialog — und die Entscheidung, welcher Hinweis ihn belegt.
 *
 * Vier Anliegen teilen sich diesen einen Platz. Ihre Rangfolge steht **nur hier**,
 * damit nicht jede Leiste die Bedingungen der anderen kennen muss:
 *
 * 1. **Eine neue Fassung der App.** Sie kann Felder und Ansichten mitbringen, die neue
 *    Karten überhaupt erst richtig darstellen — erst wenn sie eingespielt ist, hat
 *    alles Weitere einen verlässlichen Boden.
 * 2. **Geänderte Karten.** Die Meldung, um die es hier eigentlich geht.
 * 3. **Neue Funktionen der eingespielten Fassung.** Nach Platz 2 und nicht davor: Eine
 *    geänderte Einsatzunterlage wiegt schwerer als eine neue Funktion der App. Von hier
 *    führt ein Weg in den vollständigen Änderungsverlauf, der an die Stelle des
 *    Hinweises tritt.
 * 4. **Die Frage nach Benachrichtigungen.** Einmalig und nachrangig: Sie darf nie eine
 *    Meldung über tatsächlich geänderte Einsatzunterlagen verdrängen.
 *
 * Zwei Dialoge gleichzeitig gibt es nie.
 */
export function Hinweise() {
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW()

  const updates = useApp((s) => s.updates)
  const [inhaltVersteckt, setInhaltVersteckt] = useState(false)
  const [neuerungen, setNeuerungen] = useState<Aenderung[]>([])
  const [verlaufOffen, setVerlaufOffen] = useState(false)
  const [pushFrageOffen, setPushFrageOffen] = useState(false)

  useEffect(() => {
    // Fragt den Server, ob ein Dienst eingerichtet ist, und die eigene Datenbank, ob
    // schon einmal gefragt wurde. Beides darf dauern — der Dialog kommt, wenn er kommt.
    void sollPushFragen().then(setPushFrageOffen)
    void ermittleNeuerungen().then(setNeuerungen)
  }, [])

  if (needRefresh) {
    return (
      <Hinweisdialog titel="Neue Fassung verfügbar" onAbbruch={() => setNeedRefresh(false)}>
        <div className="flex items-center gap-3">
          <p className="flex-1 text-sm text-ink">
            <strong className="font-semibold">Neue Fassung verfügbar.</strong> Aktualisierte
            Karten stehen nach dem Neuladen bereit.
          </p>
          <Button variant="primary" onClick={() => void updateServiceWorker(true)}>
            Neu laden
          </Button>
          <button
            type="button"
            onClick={() => setNeedRefresh(false)}
            aria-label="Hinweis schließen"
            className="tap -mr-2 shrink-0 px-2 text-muted hover:text-ink"
          >
            Später
          </button>
        </div>
      </Hinweisdialog>
    )
  }

  if (anzahlUpdates(updates) > 0 && !inhaltVersteckt) {
    return <ContentUpdatePrompt onSpäter={() => setInhaltVersteckt(true)} />
  }

  if (verlaufOffen) {
    return <AenderungenDialog onSchliessen={() => setVerlaufOffen(false)} />
  }

  if (neuerungen.length > 0) {
    return (
      <NeuerungenPrompt
        funktionen={neuerungen}
        onErledigt={() => setNeuerungen([])}
        onAlleAenderungen={() => {
          setNeuerungen([])
          setVerlaufOffen(true)
        }}
      />
    )
  }

  if (pushFrageOffen) {
    return <PushPrompt onErledigt={() => setPushFrageOffen(false)} />
  }

  return null
}
